import CloseIcon from '@mui/icons-material/Close';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Modal, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import * as React from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IPaginatedResponse } from '../../api/httpClient';
import { IGetProjectPreviewResponse } from '../../api/project/iApiResponse';
import * as ProjectHelper from '../../api/project/projectHelper';
import { route } from '../../route/Index';
import Span from '../../shared/component/Span';
import { IProject } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { cleanAlert } from '../../store/alert/alertSlice';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { cleanImage } from '../../store/Image/imageSlice';
import { cleanProject, setProject } from '../../store/project/projectSlice';
import { cleanResult } from '../../store/result/resultSlice';
import { selectUser } from '../../store/user/userSlice';

interface IOpenProps {}

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
};

const Open = (props: IOpenProps) => {
    let pageSize = 7;

    const { t } = useTranslation();

    const inputFile = React.useRef<HTMLInputElement | null>(null);

    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState<number | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [recentProject, setRecentProject] = React.useState<IPaginatedResponse<IGetProjectPreviewResponse>>();
    const [openModal, setOpenModal] = React.useState(false);

    const handleCloseModal = () => setOpenModal(false);

    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (pageNumber) {
            fetchProject(pageNumber);
        }
    }, [pageNumber]);

    const handleGoToProject = (id: number) => {
        dispatch(cleanProject());
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());
        navigate(route.design.replace(':projectId', id.toString()));
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleOpenLocal = () => {
        inputFile.current?.click();
        setAnchorEl(null);
    };

    const fetchProject = async (page: number) => {
        setIsLoading(true);
        let res = await ProjectHelper.getProjectPreviewAsync(user.data!.email, page, pageSize);
        setRecentProject(res.data);
        setIsLoading(false);
    };

    const handleOpenCloud = () => {
        setOpenModal(true);
        if (pageNumber) {
            fetchProject(pageNumber);
        } else {
            setpageNumber(1);
        }
    };

    const handleChangeOpenLocalFile = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        e!.preventDefault();
        const reader = new FileReader();
        reader.onload = async e => {
            const text = e.target!.result as string;
            let project: IProject = JSON.parse(text);
            dispatch(setProject(project));
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
            navigate(route.design.replace(':projectId', project.id!.toString()));
        };
        reader.readAsText(e!.target.files![0]);
    };

    return (
        <Stack>
            <Button
                variant='greenLight'
                startIcon={<FolderOpenIcon />}
                id='open-button'
                endIcon={<KeyboardArrowDownIcon />}
                aria-controls={open ? 'open-menu' : undefined}
                aria-haspopup='true'
                onClick={handleClick}>
                <Span text={t('open')} />
            </Button>
            <Menu
                id='open-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'open-button',
                }}>
                <MenuItem onClick={handleOpenCloud}>{t('openCloud')}</MenuItem>
                <MenuItem onClick={handleOpenLocal}>{t('openLocal')}</MenuItem>
            </Menu>
            <input hidden type='file' accept='.gbd' ref={inputFile} onChange={handleChangeOpenLocalFile} onClick={e => ((e.target as any).value = '')} />
            <Modal open={openModal} onClose={handleCloseModal} aria-labelledby='modal-modal-title' aria-describedby='modal-modal-description'>
                <Box mx={style}>
                    <Draggable>
                        <Stack variant='modal' height='100%' direction='column' spacing={4}>
                            <Stack variant='bottomLine' direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography variant='h5' gutterBottom color={variable.greenLight}>
                                    {t('recentProjects')}
                                </Typography>
                                <IconButton aria-label='close-modal' onClick={handleCloseModal}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                            {isLoading && <CircularProgress />}
                            {!isLoading && recentProject?.value?.length! > 0 && (
                                <>
                                    <Stack flexGrow={1}>
                                        <TableContainer>
                                            <Table sx={{ minWidth: 500 }} aria-label='simple table'>
                                                <TableHead variant='greenLight'>
                                                    <TableRow>
                                                        <TableCell>{t('name')}</TableCell>
                                                        <TableCell align='right'>{t('lastUpdate')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {recentProject!.value.map((x, i) => (
                                                        <TableRow key={`${x.title}_${i}`} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                            <TableCell onClick={() => handleGoToProject(x.id)} component='th' scope='row'>
                                                                {x.title}
                                                            </TableCell>
                                                            <TableCell onClick={() => handleGoToProject(x.id)} align='right'>
                                                                {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Stack>
                                    {recentProject!.pageCount > 1 && (
                                        <Stack alignItems='end'>
                                            <Pagination
                                                showFirstButton
                                                showLastButton
                                                variant='outlined'
                                                shape='rounded'
                                                page={recentProject?.pageNumber}
                                                count={recentProject?.pageCount}
                                                onChange={(e, value) => setpageNumber(value)}
                                            />
                                        </Stack>
                                    )}
                                </>
                            )}
                            {!isLoading && recentProject?.value?.length! == 0 && <div>Empty</div>}
                        </Stack>
                    </Draggable>
                </Box>
            </Modal>
        </Stack>
    );
};

export default Open;
